import React from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import {styled} from '@mui/system';

import LinkButton from '../component/atoms/LinkButton';
import FormButton from '../component/atoms/FormButton';
import {validatePassword, validateEmail} from '../helpers/validate';
import {concatUrl} from '../helpers/url';
import history from '../helpers/history';
import {getUrlParams} from '../helpers/location';
import {getCall, postCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import {checkUserRoles} from '../helpers/authentication';


const StyledUserForm = styled(Paper)(({ theme }) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space,
    marginBottom: 30
}));

const StyledFormTitle = styled(Typography)({
    marginBottom: 20,
    fontSize: 24
});

const StyledFormControl = styled(FormControl)({
    minWidth: 200
});

const StyledErrorMessage = styled(Typography)(({ theme }) =>({
    color: theme.custom.color.error,
    fontWeight: theme.custom.font.bold,
    marginTop: 5,
    marginLeft: 15
}));

class UserForm extends React.Component {
    constructor(props) {
        super(props);

        this.params = getUrlParams();

        this.state = {
            user_id: this.params.id || null,
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            password_conf: '',
            group: '',
            organization: '',
            groups: [],
            form_errors: {}
        };

        this.form_data = {};

        this.accepted_roles = ['admin'];
        this.groups_url = concatUrl(window.ROOT_URL, 'api', 'user_groups');
        this.create_user_url = concatUrl(window.ROOT_URL, 'api', 'user', 'create');
        this.get_user_url = concatUrl(window.ROOT_URL, 'api', 'user');
        this.save_user_url = concatUrl(window.ROOT_URL, 'api', 'user', 'save');

        this.form_title = this.params.id ? 'Edit user' : 'Create user';
        this.save_button = this.params.id ? 'Save user' : 'Create user';
    }

    componentDidMount(){

        this.context.checkUserAccess(this.conditionCallback);
        this.getGroups();

        // Note: loading user data is moved into getGroupsDone to be sure the groups drop down
        // is filled before select the group of the current user.

    }

    componentDidUpdate(){
        this.props.updatedCallback();
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };


    // +++ get groups +++++++++++++++++++++++++++++++++++++++++++++++

    getGroupsFailed = () => {
        this.context.displayGlobalError('Backend service failure: Could not receive list of available user groups.');
    };

    getGroupsDone = (data, callback_failure) => {
        if (data && data.groups){
            this.setState({'groups': data.groups});

            if(this.state.user_id){
                this.loadUserData();
            }

        }else{
            callback_failure();
        }
    };

    getGroups = () => {
        getCall(this.groups_url, this.getGroupsDone, this.getGroupsFailed);
    };


    formValidated = () => {
        let form_errors = {};

        if(this.state.user_id){
            // edit existing user
            form_errors = {
                'first_name': this.state.first_name.trim().length === 0 ? 'Please enter a first name' : '',
                'last_name': this.state.last_name.trim().length === 0 ? 'Please enter a last name' : '',
                'email': validateEmail(this.state.email.trim()),
                // check the password only in case it was updated
                'password': this.state.password.trim().length === 0 ? '' : validatePassword(this.state.password, this.state.password_conf),
            };
            this.form_data = {
                user_id: this.state.user_id,
                first_name: this.state.first_name.trim(),
                last_name: this.state.last_name.trim(),
                email: this.state.email.trim(),
                group: this.state.group,
                organization: this.state.organization.trim(),
            };
            if (this.state.password.length > 0) {
                this.form_data.password = this.state.password;
            }
        }else{
            // create new user
            form_errors = {
                'first_name': this.state.first_name.trim().length === 0 ? 'Please enter a first name' : null,
                'last_name': this.state.last_name.trim().length === 0 ? 'Please enter a last name' : null,
                'email': validateEmail(this.state.email.trim()),
                'password': validatePassword(this.state.password, this.state.password_conf),
                'group': this.state.group.length === 0 ? 'Please select a group' : null,
            };
            this.form_data = {
                first_name: this.state.first_name.trim(),
                last_name: this.state.last_name.trim(),
                email: this.state.email.trim(),
                password: this.state.password,
                group: this.state.group,
                organization: this.state.organization.trim(),
            };
        }
        this.setState({form_errors: form_errors});
        let form_is_valid = true;

        /*eslint-disable no-unused-vars */
        for (const key in form_errors){
            if (form_errors[key]){
                form_is_valid = false;
                break;
            }
        }
        /*eslint-enabled no-unused-vars */

        return form_is_valid;
    };


    // +++ save user +++++++++++++++++++++++++++++++++++++++++++++++

    saveUserFailed = () => {
        this.context.displayGlobalError('Backend service failure: Could not save the user data.');
    };

    saveUserDone = (data) => {
        if (data && data.error){
            this.setState({form_errors: {'email': data.error}});
        }else {
            history.push('/users');
            document.location.reload();
        }
    };

    saveUser = () => {
        if (this.formValidated()){
            const url = this.state.user_id ? this.save_user_url : this.create_user_url;
            postCall(url, this.form_data, this.saveUserDone, this.saveUserFailed);
        }
    };


    // +++ load user data +++++++++++++++++++++++++++++++++++++++++++++++

    loadUserDataFailed = () => {
        this.context.displayGlobalError('Backend service failure: Could not load user data.');
    };

    loadUserDataDone = (data, callback_failure) => {
        if (data){
            this.setState({
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                group: data.group,
                organization: data.organization
            });
        }else{
            callback_failure();
        }
    };

    loadUserData = () => {
        getCall(`${this.get_user_url}?id=${this.state.user_id}`, this.loadUserDataDone, this.loadUserDataFailed);
    };


    showErrorMessage = (key) => {
        if (key in this.state.form_errors && this.state.form_errors[key]) {
            return (
                <StyledErrorMessage data-bt={`error_${key}`}>
                    {this.state.form_errors[key]}
                </StyledErrorMessage>
            );
        }
    };

    errorMode = (key) => {
        return key in this.state.form_errors && this.state.form_errors[key] !== null;
    };

    render() {

        let match_explanation = [
            "Score 0.792 - the property score based on values also in the search profile, the specificity of the values in the whole data set and the importance of the property",
            "Base score 0.784 - computed using IDF of matching values and aggregating the results.",
            "Hard score 0.784 - computed using the hard factor 1.0 applied on base score and eventually limited to a maximum.",
        ]
        let match_details_info = {
            data:{
                hard_score:0.7838698100479415,
                hard_factor:1,
                soft_score:0.7838698100479415,
                soft_factor:1,
                base_score:0.7838698100479415,
                matched_count:1
            }
        }

        return (
            <GlobalContext.Consumer>{() => (
                <form>
                    <StyledUserForm data-bt="user_form">
                        <StyledFormTitle>
                            {this.form_title}
                        </StyledFormTitle>
                        <Grid container spacing={5}>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('first_name')}
                                        variant="outlined"
                                        id="first_name"
                                        type="text"
                                        label="First name"
                                        slotProps={{ htmlInput: {'data-bt': 'first_name'} }}
                                        value={this.state.first_name}
                                        onChange={(event) => {
                                            this.setState({first_name: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('first_name')}
                                </StyledFormControl>
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('last_name')}
                                        variant="outlined"
                                        id="last_name"
                                        type="text"
                                        label="Last name"
                                        slotProps={{ htmlInput: {'data-bt': 'last_name'} }}
                                        value={this.state.last_name}
                                        onChange={(event) => {
                                            this.setState({last_name: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('last_name')}
                                </StyledFormControl>
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('email')}
                                        variant="outlined"
                                        id="email"
                                        type="text"
                                        autoComplete="off"
                                        label="Email (username)"
                                        slotProps={{ htmlInput: {'data-bt': 'email'} }}
                                        value={this.state.email}
                                        onChange={(event) => {
                                            this.setState({email: event.target.value});
                                        }}
                                    />
                                </StyledFormControl>
                                {this.showErrorMessage('email')}
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl required variant="outlined">
                                    <InputLabel id="group-label" data-bt="group">
                                        Group
                                    </InputLabel>
                                    <Select
                                        labelId="group-label"
                                        label="Group"
                                        variant="outlined"
                                        error={this.errorMode('group')}
                                        value={this.state.group}
                                        onChange={(event) => {
                                            this.setState({group: event.target.value});
                                        }}
                                    >
                                    {this.state.groups.map((group) => <MenuItem value={group.key} key={group.key} data-bt="user_group_item">
                                                {group.label}</MenuItem>)}
                                    </Select>
                                </StyledFormControl>
                                {this.showErrorMessage('group')}
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('password')}
                                        variant="outlined"
                                        id="password"
                                        type="password"
                                        autoComplete="new-password"
                                        label="Password"
                                        slotProps={{ htmlInput: {'data-bt': 'password'} }}
                                        value={this.state.password}
                                        onChange={(event) => {
                                            this.setState({password: event.target.value});
                                        }}
                                    />
                                </StyledFormControl>
                                {this.showErrorMessage('password')}
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('password')}
                                        variant="outlined"
                                        id="password_conf"
                                        type="password"
                                        autoComplete="off"
                                        label="Password confirmation"
                                        slotProps={{ htmlInput: {'data-bt': 'password_conf'} }}
                                        value={this.state.password_conf}
                                        onChange={(event) => {
                                            this.setState({password_conf: event.target.value});
                                        }}
                                    />
                                </StyledFormControl>
                            </Grid>
                            <Grid size={12}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        variant="outlined"
                                        id="organization"
                                        type="text"
                                        label="Organization"
                                        slotProps={{ htmlInput: {'data-bt': 'organization'} }}
                                        value={this.state.organization}
                                        onChange={(event) => {
                                            this.setState({organization: event.target.value});
                                        }}
                                    />
                                </StyledFormControl>
                                {this.showErrorMessage('organization')}
                            </Grid>
                            <Grid size={12}>
                                <FormButton text={this.save_button} dataBt="create" actionCallback={this.saveUser} />
                                <LinkButton text="Cancel" dataBt="cancel" linkData={{pathname: '/users'}} />
                            </Grid>
                        </Grid>
                    </StyledUserForm>
                </form>
            )}
            </GlobalContext.Consumer>
        );
    }
}

UserForm.contextType = GlobalContext;

UserForm.propTypes = {
    updatedCallback: PropTypes.func
};

export default UserForm;
